import React from 'react';
import { CanvasToolbar } from './CanvasToolbar.jsx';
import { KaraokeOverlayPreview } from './KaraokeOverlayPreview.jsx';

export function SplitViewCanvas({
  beforeSrc,
  afterSrc,
  splitPos,
  containerRef,
  onMouseDown,
  onTouchStart,
  onExport,
  onSendToUpscaler,
  onSendToRelight,
  onCopyPrompt,
  onReroll,
  onLaunchMetaAds,
  isPlaying,
  onTogglePlay,
  zoomLevel = 1,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  isFullscreen,
  onToggleFullscreen,
  isVideo = false,
  videoTime = 0,
  onSeekVideo,
  activeLightDirection = null,
  karaokeWords = null,
  captionAccent = 'neon',
  entranceZoomActive = false,
  showSafeZones = false,
  onToggleSafeZones
}) {
  const isVideoAsset = isVideo || afterSrc?.endsWith('.mp4') || beforeSrc?.endsWith('.mp4');

  const [videoLoadError, setVideoLoadError] = React.useState(false);

  React.useEffect(() => {
    setVideoLoadError(false);
  }, [afterSrc, beforeSrc]);

  return (
    <main style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: '#090a0f',
      position: isFullscreen ? 'fixed' : 'relative',
      inset: isFullscreen ? 0 : 'auto',
      zIndex: isFullscreen ? 100 : 'auto',
      width: isFullscreen ? '100vw' : 'auto',
      height: isFullscreen ? '100vh' : 'auto',
      overflow: 'hidden'
    }}>
      {/* Top Floating Badge */}
      <div style={{ position: 'absolute', top: '20px', left: '24px', zIndex: 30 }}>
        <div style={{
          background: 'rgba(15, 17, 24, 0.75)',
          backdropFilter: 'blur(12px)',
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.1)',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#a855f7' }}></span>
          <span>Modo Comparación: <strong>Original vs Veo 4K Enhanced</strong></span>
        </div>
      </div>

      {/* Floating Action Toolbar */}
      <div style={{ position: 'absolute', top: '20px', right: '24px', zIndex: 30 }}>
        <CanvasToolbar
          onExport={onExport}
          onSendToUpscaler={onSendToUpscaler}
          onSendToRelight={onSendToRelight}
          onCopyPrompt={onCopyPrompt}
          onReroll={onReroll}
          onLaunchMetaAds={onLaunchMetaAds}
          isPlaying={isPlaying}
          onTogglePlay={onTogglePlay}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onResetZoom={onResetZoom}
          isFullscreen={isFullscreen}
          onToggleFullscreen={onToggleFullscreen}
          showSafeZones={showSafeZones}
          onToggleSafeZones={onToggleSafeZones}
        />
      </div>

      {/* Viewport Frame */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isFullscreen ? '20px' : '40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div
          ref={containerRef}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          style={{
            width: isFullscreen ? '88vw' : '680px',
            height: isFullscreen ? '85vh' : '680px',
            maxWidth: '94%',
            maxHeight: isFullscreen ? '90vh' : '80vh',
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(0,0,0,0.8), 0 0 30px rgba(168, 85, 247, 0.2)',
            border: '1px solid rgba(255,255,255,0.1)',
            userSelect: 'none',
            cursor: 'ew-resize',
            transform: `scale(${zoomLevel})`,
            transition: 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)'
          }}
        >
          {isVideoAsset ? (
            !videoLoadError ? (
              <video
                src={afterSrc}
                autoPlay
                loop
                muted
                playsInline
                onError={() => {
                  console.warn('[SplitViewCanvas] Error decodificando video:', afterSrc);
                  setVideoLoadError(true);
                }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#0a0d14',
                color: '#94a3b8',
                padding: '24px',
                textAlign: 'center',
                gap: '12px'
              }}>
                <div style={{ fontSize: '32px' }}>🎬</div>
                <div style={{ fontWeight: 600, color: '#f1f5f9' }}>Video en procesamiento o decodificación</div>
                <div style={{ fontSize: '12px', maxWidth: '360px', lineHeight: 1.5 }}>
                  El archivo multimedia se está descargando o verificando en almacenamiento persistente.
                </div>
              </div>
            )
          ) : (
            <img src={afterSrc} alt="Enhanced" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${splitPos}%`,
            overflow: 'hidden',
            borderRight: '2px solid #ffffff'
          }}>
            {isVideoAsset ? (
              <video
                src={beforeSrc}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: containerRef.current ? `${containerRef.current.clientWidth}px` : '680px',
                  height: '100%',
                  maxWidth: 'none',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <img
                src={beforeSrc}
                alt="Original"
                style={{
                  width: containerRef.current ? `${containerRef.current.clientWidth}px` : '680px',
                  height: '100%',
                  maxWidth: 'none',
                  objectFit: 'cover'
                }}
              />
            )}
            <div style={{
              position: 'absolute',
              bottom: '16px',
              left: '16px',
              background: 'rgba(0,0,0,0.65)',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 700,
              color: '#e2e8f0'
            }}>
              ORIGINAL
            </div>
          </div>


          <div style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            background: 'rgba(168, 85, 247, 0.85)',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#ffffff'
          }}>
            MAGNIFIC ENHANCED (VEO 4K)
          </div>

          {/* Orbe de Luz 3D Interactivo para Relight Engine */}
          {activeLightDirection && (
            <div style={{
              position: 'absolute',
              ...(activeLightDirection === 'left' ? { left: '10%', top: '35%' } : {}),
              ...(activeLightDirection === 'right' ? { right: '10%', top: '35%' } : {}),
              ...(activeLightDirection === 'top' ? { left: '50%', top: '8%', transform: 'translateX(-50%)' } : {}),
              ...(activeLightDirection === 'back' ? { left: '50%', top: '25%', transform: 'translateX(-50%)' } : {}),
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, #ffffff 10%, rgba(251,191,36,0.8) 50%, rgba(251,191,36,0) 80%)',
              boxShadow: '0 0 35px #fbbf24, 0 0 70px #f59e0b',
              border: '2px solid rgba(255,255,255,0.9)',
              pointerEvents: 'none',
              animation: 'pulse 2s infinite',
              zIndex: 25
            }}>
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.8)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '9px',
                fontWeight: 700,
                color: '#fbbf24',
                whiteSpace: 'nowrap'
              }}>
                FOCO 3D
              </div>
            </div>
          )}

          {/* Karaoke Captions Overlay Preview */}
          {karaokeWords && karaokeWords.length > 0 && (
            <KaraokeOverlayPreview
              words={karaokeWords}
              accent={captionAccent}
              isPlaying={isPlaying}
            />
          )}

          {/* Plantilla Oficial de Zonas Seguras de Meta (Reels & Stories Safe Zones) */}
          {showSafeZones && (
            <div style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 35,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              {/* 1. Zona Prohibida Superior (Header de Instagram/Facebook ~14%) */}
              <div style={{
                height: '14%',
                background: 'rgba(239, 68, 68, 0.22)',
                borderBottom: '2px dashed rgba(239, 68, 68, 0.85)',
                backdropFilter: 'blur(1px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 14px',
                color: '#fca5a5',
                fontSize: '10px',
                fontWeight: 800
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#ef4444' }} />
                  <span>⛔ HEADER META (Perfil, Cuenta y Barra)</span>
                </div>
                <span style={{ background: 'rgba(0,0,0,0.6)', padding: '2px 6px', borderRadius: '4px' }}>Top 14% Oculto</span>
              </div>

              {/* 2. Cuerpo Central: Zona Segura + Iconos Laterales */}
              <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
                {/* Cuadro Central: Safe Zone 100% visible */}
                <div style={{
                  flex: 1,
                  margin: '8px 12px',
                  border: '2px dashed #10b981',
                  borderRadius: '10px',
                  background: 'rgba(16, 185, 129, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px'
                }}>
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.95)',
                    color: '#ffffff',
                    fontSize: '11px',
                    fontWeight: 800,
                    padding: '4px 14px',
                    borderRadius: '20px',
                    letterSpacing: '0.5px',
                    boxShadow: '0 0 16px rgba(16, 185, 129, 0.6)'
                  }}>
                    ✅ ZONA SEGURA (SAFE ZONE): Rostro, Hook y Subtítulos
                  </div>
                </div>

                {/* Franja Lateral Derecha de Iconos de Meta (Like, Comentarios, Compartir ~10%) */}
                <div style={{
                  width: '52px',
                  background: 'rgba(239, 68, 68, 0.22)',
                  borderLeft: '2px dashed rgba(239, 68, 68, 0.85)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  color: '#fca5a5',
                  fontSize: '10px',
                  fontWeight: 800
                }}>
                  <span>❤️</span>
                  <span>💬</span>
                  <span>↗️</span>
                  <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '8px', opacity: 0.85 }}>
                    ⛔ Botones
                  </span>
                </div>
              </div>

              {/* 3. Zona Prohibida Inferior (Copy + Botón de WhatsApp ~28%) */}
              <div style={{
                height: '28%',
                background: 'rgba(239, 68, 68, 0.25)',
                borderTop: '2px dashed rgba(239, 68, 68, 0.85)',
                backdropFilter: 'blur(1px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '10px 14px',
                color: '#fca5a5'
              }}>
                <div style={{
                  background: 'rgba(0,0,0,0.75)',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(239,68,68,0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{ fontSize: '9px', color: '#f87171', fontWeight: 800, letterSpacing: '0.4px' }}>
                    ⛔ ZONA TAPADA POR META (~28% INFERIOR)
                  </div>
                  <div style={{ fontSize: '10px', color: '#e2e8f0', fontWeight: 600 }}>
                    @NombreEmpresa • Texto de Copy Publicitario...
                  </div>
                  <div style={{
                    marginTop: '2px',
                    background: '#25d366',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '10px',
                    padding: '5px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    boxShadow: '0 0 10px rgba(37, 211, 102, 0.4)'
                  }}>
                    📲 [ Botón de Acción: Enviar mensaje de WhatsApp ]
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Center Handle */}
          <div style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${splitPos}%`,
            width: '2px',
            background: '#ffffff',
            pointerEvents: 'none',
            transform: 'translateX(-50%)'
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(15, 17, 24, 0.9)',
              border: '2px solid #ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px'
            }}>
              <div style={{ width: '2px', height: '12px', background: '#cbd5e1' }} />
              <div style={{ width: '2px', height: '12px', background: '#cbd5e1' }} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
